import { fetchAdditionalFields, fetchLocations, fetchEvents, fetchUnits, fetchServiceAvailability } from "../API";
import { DATE_FORMAT, FIRST_OPTION, SELECTORS } from "../Constants";
import { events, locations, event, unit, units, additionalField, Location, timeSlot, timeSlots } from "../API/types";
import { Dropdown } from "../../../Components/Form/Dropdown";
import { InputText } from "../../../Components/Form/Input";
import { PickerDate } from "../../../Components/Form/PickerDate";
import { File } from "../../../Components/Form/File";
import LoaderOverlay from "../../LoaderOverlay";
import { DateTime } from "@easepick/bundle";
import { TextArea } from "../../../Components/Form/TextArea";
import { AbstractFormElement } from "../../../Components/Form/Abstract";
import Confetti from "../../RentalAndAppointments/Confetti";
import { CONFETTI_OPTIONS } from "../../RentalAndAppointments/Constants";
import { Checkbox } from "../../../Components/Form/Checkbox";
import { Toast } from "../../../Components/Dialog/Toast";
import { IRadioGroupOption, RadioGroup } from "../../../Components/Form/RadioGroup";
import { DialogListener } from "../../../Components/Dialog/DialogListener";
import { I18n } from "./I18n";

const FIELDS = {
    EVENT: 1 << 0,
    LOCATION: 1 << 1,
    UNIT: 1 << 2,
    DATE: 1 << 3,
    TIME: 1 << 4,
    ADDITIONAL: 1 << 5,
    ALL: (1 << 0) | (1 << 1) | (1 << 2) | (1 << 3) | (1 << 4) | (1 << 5) | (1 << 6) | (1 << 7)
};

export class Form {
    private readonly form: HTMLFormElement;
    private readonly i18n: I18n;
    private readonly loaderOverlay: LoaderOverlay;
    private readonly selectEvent: Dropdown | RadioGroup;
    private readonly selectLocation: Dropdown;
    private readonly selectTime: Dropdown;
    private readonly inputDate: PickerDate;
    private readonly inputEndDate: InputText;
    private readonly inputEndTime: InputText;
    private selectUnit: Dropdown | InputText;
    private readonly confetti = new Confetti(CONFETTI_OPTIONS.count, CONFETTI_OPTIONS.defaults);
    private availableTimeslots: timeSlots = null;

    private handleEventChange: (e?: Event) => void;
    private handleLocationChange: (e?: Event) => void;
    private handleUnitChange: (e?: Event) => void;
    private handleDateChange: (e?: Event) => void;
    private handleTimeChange: (e?: Event) => void;
    private handleSubmit: (e?: Event) => void;

    constructor(form: HTMLFormElement) {
        this.form = form;
        this.i18n = new I18n();
        this.selectEvent = this.form.querySelector<Dropdown | RadioGroup>(SELECTORS.selectService);
        this.selectLocation = this.form.querySelector<Dropdown>(SELECTORS.selectLocation);
        this.selectTime = this.form.querySelector<Dropdown>(SELECTORS.selectTime);
        this.inputDate = this.form.querySelector<PickerDate>(SELECTORS.inputDate);
        this.inputEndDate = this.form.querySelector<InputText>(SELECTORS.inputEndDate);
        this.inputEndTime = this.form.querySelector<InputText>(SELECTORS.inputEndTime);
        this.loaderOverlay = new LoaderOverlay();
        this.initListeners();
        form.sb = this;
    }

    /**
     * initialize event listeners
     * @private
     */
    private initListeners() {
        this.handleEventChange = this.eventChangeHandler.bind(this);
        this.handleLocationChange = this.locationChangeHandler.bind(this);
        this.handleUnitChange = this.unitChangeHandler.bind(this);
        this.handleDateChange = this.dateChangedHandler.bind(this);
        this.handleTimeChange = this.timeChangeHandler.bind(this);
        this.handleSubmit = this.submitHandler.bind(this);
    }

    /**
     * initialize form
     */
    public async init(): Promise<void> {
        this.addListeners();
        this.loaderOverlay.render();
        let dispatchChange = false;
        try {
            const events = await fetchEvents(this.form.dataset.category);
            if (this.event) {
                this.selectEvent.dataset.duration = String(events.filter((e) => e.id === this.event)?.[0].duration);
                dispatchChange = true;
            } else {
                this.events = events;
            }
        } catch (error) {
            this.handleError(error);
        } finally {
            this.loaderOverlay.destroy();
            if (dispatchChange) {
                this.selectEvent.dispatchEvent(new Event("change"));
            }
        }
    }

    /**
     * add event listeners
     * @private
     */
    private addListeners() {
        this.selectEvent.addEventListener("change", this.handleEventChange);
        this.selectLocation.addEventListener("change", this.handleLocationChange);
        this.inputDate.addEventListener("input", this.handleDateChange);
        this.selectTime.addEventListener("change", this.handleTimeChange);
        this.form.addEventListener("submit", this.handleSubmit);
    }

    /**
     * handle event change
     * @param e
     * @private
     */
    private async eventChangeHandler(e: Event): Promise<void> {
        this.updateFields(e);
        const event = (e.currentTarget as Dropdown | HTMLFormElement).value;
        this.loaderOverlay.render();
        try {
            this.locations = await fetchLocations(event);
            this.additionalFields = await fetchAdditionalFields(event);
        } catch (error) {
            this.handleError(error);
        } finally {
            this.loaderOverlay.destroy();
        }
    }

    /**
     * handle location change
     * @private
     */
    private async locationChangeHandler(e: Event): Promise<void> {
        this.updateFields(e);
        if (this.event && this.location) {
            this.loaderOverlay.render();
            try {
                this.units = await fetchUnits(this.event, this.location);
            } catch (error) {
                this.handleError(error);
            } finally {
                this.loaderOverlay.destroy();
            }
        }
    }

    /**
     * handle unit change
     * @private
     */
    private async unitChangeHandler(e: Event): Promise<void> {
        try {
            this.loaderOverlay.render();
            const from = new DateTime();
            this.availableTimeslots = (await fetchServiceAvailability(from, this.event, this.unit)) as timeSlots;
            const locked = [];
            for (const date of Object.keys(this.availableTimeslots)) {
                if (this.availableTimeslots[date].length === 0) {
                    locked.push(date);
                }
            }
            this.inputDate.dataset.locked = locked.join(",");
        } catch (error) {
            this.handleError(error);
        } finally {
            this.updateFields(e);
            this.loaderOverlay.destroy();
        }
    }

    /**
     * handle date change
     * @param e
     * @private
     */
    private async dateChangedHandler(e: CustomEvent): Promise<void> {
        this.updateFields(e);
        if (this.event && this.location && this.unit && this.date) {
            this.loaderOverlay.render();
            try {
                this.inputEndDate.value = this.date.format(DATE_FORMAT.kebab);
                this.timeSlots = this.availableTimeslots[(this.date as DateTime).format(DATE_FORMAT.kebab)];
                this.selectTime.disabled = false;
            } catch (error) {
                this.handleError(error);
            } finally {
                this.loaderOverlay.destroy();
            }
        }
    }

    /**
     * handle time change
     * @private
     */
    private async timeChangeHandler(): Promise<void> {
        if (this.event && this.location && this.unit && this.date && this.time) {
            const dt = `${this.inputDate.value} ${this.selectTime.value}`;
            const tmp = new Date(dt).valueOf() + 1000 * 60 * this.eventDuration;
            this.inputEndTime.value = new DateTime(tmp).format("HH:mm:ss");
        }
    }

    /**
     * handle submit
     * @private
     */
    private async submitHandler(e: Event): Promise<void> {
        this.loaderOverlay.render();
        e.preventDefault();
        try {
            const formData = new FormData(this.form);
            const body = new URLSearchParams(formData.entries() as unknown as string[][]);
            const result = await fetch(this.form.action, { method: "POST", body });
            if (result.status === 200) {
                this.confetti.render();
                new Toast({
                    title: window.SuccessMessages.bookingRequest.success,
                    type: Toast.TYPE_SUCCESS
                })?.render();
                this.updateFields(e);
            } else {
                new Toast({
                    title: window.SuccessMessages.bookingRequest.error,
                    type: Toast.TYPE_ERROR
                })?.render();
            }
        } catch (error) {
            this.handleError(error);
        } finally {
            this.loaderOverlay.destroy();
        }
    }

    /**
     * reset, en- or disable form fields and values
     * @private
     */
    private updateFields(e: Event) {
        let fields = FIELDS.ALL;
        let formData, name, email;
        switch (e.target) {
            case this.form:
                formData = new FormData(this.form);
                name = formData.get("name") as string;
                email = formData.get("email") as string;
                this.form.reset();
                (this.form.querySelector('x-input[name="name"]') as InputText).value = name;
                (this.form.querySelector('x-input[name="email"]') as InputText).value = email;
                if (this.eventPreselection) {
                    this.selectEvent.removeEventListener("change", this.handleEventChange);
                    this.selectEvent.value = String(this.eventPreselection);
                    this.selectEvent.addEventListener("change", this.handleEventChange);
                } else {
                    this.additionalFields = [];
                }
                break;
            case this.selectEvent:
                fields &= ~FIELDS.EVENT;
                break;
            case this.selectLocation:
                fields &= ~FIELDS.EVENT & ~FIELDS.LOCATION;
                break;
            case this.selectUnit:
                fields &= ~FIELDS.EVENT & ~FIELDS.LOCATION & ~FIELDS.UNIT;
                break;
            case this.inputDate:
                fields &= ~FIELDS.EVENT & ~FIELDS.LOCATION & ~FIELDS.UNIT & ~FIELDS.DATE;
                break;
        }
        if ((fields & FIELDS.LOCATION) != 0) {
            this.location = "";
            if (!this.eventPreselection) {
                this.locations = [];
            }
        }
        if ((fields & FIELDS.UNIT) != 0) {
            this.unit = "";
            this.units = [];
        }
        if ((fields & FIELDS.DATE) != 0) {
            this.date = "";
        }
        if ((fields & FIELDS.TIME) != 0) {
            this.time = "";
            this.timeSlots = [];
        }
        this.selectLocation.disabled = !this.event;
        this.selectUnit && (this.selectUnit.disabled = !this.event || !this.location);
        this.inputDate.disabled = !this.event || !this.location || !this.unit;
        this.selectTime.disabled = !this.event || !this.location || !this.unit || !this.date;
    }

    /**
     * handle exceptions
     * @param error
     * @private
     */
    private handleError(error: unknown) {
        this.resetSelection();
        new Toast({
            title: window.SuccessMessages.bookingRequest.error,
            type: Toast.TYPE_ERROR
        })?.render();
        console.error(error);
    }

    public resetSelection(): void {
        this.selectEvent.value = "";
        this.location = "";
        this.unit = "";
        this.date = "";
        this.time = "";
    }

    /**
     * set and render events data
     * @param v
     * @private
     */
    private set events(v: events) {
        this.selectEvent.removeEventListener("change", this.handleEventChange);
        const translations = this.i18n.getEvents(v);

        if (this.selectEvent.tagName === "X-DROPDOWN") {
            this.selectEvent.options.forEach((o: HTMLOptionElement) => {
                if (o.value) {
                    this.selectEvent.removeChild(o);
                }
            });
            this.selectEvent.appendChild(Form.getFirstOption());
            v.forEach((e: event) => {
                const option = new Option(translations[e.id]?.name || e.name, e.id);
                option.dataset.units = JSON.stringify(e.providers);
                option.dataset.duration = String(e.duration);
                this.selectEvent.appendChild(option);
            });
            (<Dropdown>this.selectEvent).selectedIndex = -1;
        } else if (this.selectEvent.tagName === "X-RADIO-GROUP") {
            v.forEach((e) => {
                const option: IRadioGroupOption = {
                    label: translations[e.id]?.name || e.name,
                    value: e.id,
                    dataset: {
                        duration: String(e.duration)
                    }
                };
                (this.selectEvent as RadioGroup).initOption(option);
            });
        }

        const eventsHeader = this.selectEvent.parentNode.querySelector("h3");
        const modalId = `booking-form-${this.form.dataset.category}`;
        const icon = eventsHeader.querySelector<HTMLSpanElement>("span.icon.info") || document.createElement("span");
        if (!icon.dataset.modalReveal) {
            let modalContent = "";
            v.forEach((e: event) => {
                if (e.description) {
                    modalContent += `<div><div class="label">${translations[e.id]?.name || e.name}</div><p>${
                        translations[e.id]?.description || e.description
                    }</p></div>`;
                }
            });
            if (modalContent) {
                modalContent = `<div class="booking-form-modal">${modalContent}</div>`;
                icon.classList.add("icon", "info");
                icon.dataset.modalReveal = modalId;
                icon.dataset.title = this.form.dataset.modalHeader;
                const template = document.getElementById(modalId) || document.createElement("template");
                document.body.appendChild(template);
                template.id = modalId;
                template.innerHTML = modalContent;
                this.selectEvent.parentNode.querySelector("h3").appendChild(icon);
                new DialogListener(icon);
            }
        }

        this.selectEvent.addEventListener("change", this.handleEventChange);
    }

    /**
     * set and render locations data
     * @param v
     * @private
     */
    private set locations(v: locations) {
        const translations = this.i18n.getLocations(v);
        this.selectLocation.removeEventListener("change", this.handleLocationChange);
        this.selectLocation.options.forEach((o: HTMLOptionElement) => {
            this.selectLocation.removeChild(o);
        });
        this.selectLocation.appendChild(Form.getFirstOption());
        v.forEach((l: Location) => {
            const option = new Option(translations[l.id]?.title || l.title, l.id);
            option.dataset.events = JSON.stringify(l.events);
            option.dataset.units = JSON.stringify(l.units);
            this.selectLocation.appendChild(option);
        });
        this.selectLocation.selectedIndex = -1;
        this.selectLocation.addEventListener("change", this.handleLocationChange);
    }

    /**
     * set and render units data
     * if only one unit is available render a hidden form field instead of a dropdown
     * @param v
     * @private
     */
    private set units(v: units) {
        const translations = this.i18n.getUnits(v);
        if (this.selectUnit) {
            this.selectUnit.removeEventListener("change", this.handleUnitChange);
            this.selectUnit.remove();
        }
        if (v.length > 0) {
            if (v.length > 1) {
                this.selectUnit = document.createElement("x-dropdown") as Dropdown;
            } else {
                this.selectUnit = document.createElement("x-input") as InputText;
            }
            this.selectLocation.parentNode.appendChild(this.selectUnit);
            this.selectUnit.addEventListener("change", this.handleUnitChange);
            this.selectUnit.name = "unit";
            this.selectUnit.setAttribute("placeholder", this.form.dataset.unitName);
            if (this.selectUnit instanceof Dropdown) {
                this.selectUnit.appendChild(Form.getFirstOption());
                v.forEach((u: unit) => {
                    const option = new Option(translations[u.id]?.name || u.name, u.id);
                    option.dataset.events = u.services ? JSON.stringify(u.services) : "all";
                    this.selectUnit.appendChild(option);
                });
                this.selectUnit.selectedIndex = -1;
            } else {
                this.selectUnit.type = "hidden";
                this.selectUnit.setAttribute("type", "hidden");
                this.selectUnit.value = v[0].id;
                setTimeout(() => this.selectUnit.dispatchEvent(new Event("change")), 0);
            }
        }
    }

    /**
     * set and render timeslots
     * @param v
     * @private
     */
    private set timeSlots(v: timeSlot[]) {
        this.selectTime.removeEventListener("change", this.handleTimeChange);
        this.selectTime.options.forEach((o: HTMLOptionElement) => {
            if (o.value) {
                this.selectTime.removeChild(o);
            }
        });
        if (v.length && this.date) {
            this.selectTime.appendChild(Form.getFirstOption());
            v.forEach((t) => {
                const option = new Option(t.replace(/:\d{2}$/, ""), t);
                this.selectTime.appendChild(option);
            });
        }
        this.selectTime.addEventListener("change", this.handleTimeChange);
    }

    /**
     * set and render additional fields data
     * @param v
     * @private
     */
    private set additionalFields(v: additionalField[]) {
        const currentFields = [...this.form.querySelectorAll<AbstractFormElement>(SELECTORS.additionalFields)];
        // remove if field is rendered but not present in data
        currentFields
            .filter(
                (cf) =>
                    !v.find((af) => {
                        const name = `additional[${af.name}]`;
                        return cf.name === name || cf.dataset.name === name;
                    })
            )
            .forEach((l) => l.remove());
        if (v.length > 0) {
            const fieldset = this.form.querySelector(SELECTORS.additional);
            const translations = this.i18n.getAdditionalFields(v);

            v.forEach((field) => {
                const fieldName = `additional[${field.name}]`;
                // return if field is already rendered
                if (currentFields.find((cf) => cf.name === fieldName || cf.dataset.name === fieldName)) return;
                let element: Dropdown | TextArea | Checkbox | InputText | PickerDate | File | HTMLDivElement;
                if (field.type === "textarea") {
                    // text multiline
                    element = document.createElement("x-textarea") as TextArea;
                }
                if (field.type === "select") {
                    element = document.createElement("x-dropdown") as Dropdown;
                }
                if (field.type === "checkbox") {
                    element = document.createElement("x-checkbox") as Checkbox;
                }
                if (field.type === "date") {
                    element = document.createElement("x-date-picker") as PickerDate;
                }
                if (field.type === "static" && field.default) {
                    element = document.createElement("div");
                }
                if (field.type === "text" || field.type === "digits") {
                    // text single line
                    element = document.createElement("x-input") as InputText;
                }
                if (element) {
                    fieldset.appendChild(element);
                    if (element instanceof Dropdown) {
                        element.appendChild(Form.getFirstOption());
                        translations[field.id].values.split(",").forEach((v) => {
                            element.appendChild(
                                new Option(
                                    v,
                                    v,
                                    translations[field.id].default === v,
                                    translations[field.id].default === v
                                )
                            );
                        });
                        if (translations[field.id].value || translations[field.id].default) {
                            element.value = translations[field.id].value || translations[field.id].default;
                        }
                        element.selectedIndex = -1;
                    }
                    if (element instanceof Checkbox) {
                        element.value = "1";
                    }
                    if (
                        element instanceof Checkbox ||
                        element instanceof TextArea ||
                        element instanceof Dropdown ||
                        element instanceof InputText
                    ) {
                        element.setAttribute("placeholder", translations[field.id].title);
                    }

                    if (field.type === "digits" && element instanceof InputText) {
                        element.type = "number";
                    }

                    if (element instanceof AbstractFormElement) {
                        element.name = fieldName;
                        if (!field.is_null || field.is_null === "0") {
                            element.required = true;
                        }
                    }

                    if (field.type === "static" && element instanceof HTMLDivElement) {
                        element.dataset.name = fieldName;
                        element.innerHTML = translations[field.id].default;
                    }
                }
            });
        }
    }

    /**
     * obtain 'please choose' option
     * @private
     */
    private static getFirstOption(): HTMLOptionElement {
        const option = document.createElement("option");
        option.innerHTML = FIRST_OPTION.label;
        option.value = String(FIRST_OPTION.value);
        option.disabled = true;
        return option;
    }

    /**
     * event preselection getter
     */
    public get eventPreselection() {
        return Number(this.form.dataset.eventPreselection);
    }

    /**
     * event property getter
     */
    public get event() {
        return this.selectEvent.value;
    }

    /**
     * event option property getter
     * @private
     */
    private get eventOption() {
        return this.selectEvent.options[this.selectEvent.selectedIndex] as HTMLOptionElement;
    }

    /**
     * event duration property getter
     */
    public get eventDuration() {
        if (this.eventPreselection) {
            return Number(this.selectEvent.dataset.duration);
        }
        return Number(this.eventOption?.dataset.duration);
    }

    /**
     * location property getter/setter
     */
    public get location() {
        return this.selectLocation.value;
    }

    public set location(v) {
        this.selectLocation.removeEventListener("change", this.handleLocationChange);
        if ((!v && this.location) || v) {
            this.selectLocation.value = v;
        }
        this.selectLocation.addEventListener("change", this.handleLocationChange);
    }

    /**
     * unit property getter/setter
     */
    public get unit() {
        return this.selectUnit?.value;
    }

    public set unit(v) {
        if ((!v && this.unit) || v) {
            this.selectUnit.value = v;
        }
    }

    /**
     * date property getter/setter
     */
    public get date() {
        return this.inputDate.value && new DateTime(this.inputDate.value);
    }

    public set date(v: DateTime | "") {
        if (v) {
            this.inputDate.value = v.format(DATE_FORMAT.kebab);
        } else {
            this.inputDate.value = "";
        }
    }

    /**
     * time property getter/setter
     */
    public get time() {
        return this.selectTime.value;
    }

    public set time(v) {
        this.selectTime.removeEventListener("change", this.handleTimeChange);
        if ((!v && this.time) || v) {
            this.selectTime.value = v;
        }
        this.selectTime.addEventListener("change", this.handleTimeChange);
    }
}
